/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { LexicalEditor, NodeKey } from "lexical";

import {
  $createCodeNode,
  $isCodeNode,
  CODE_LANGUAGE_FRIENDLY_NAME_MAP,
  CODE_LANGUAGE_MAP,
  getLanguageFriendlyName,
} from "@lexical/code";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isDecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import { $createQuoteNode, $isHeadingNode } from "@lexical/rich-text";
import { $isParentElementRTL, $selectAll, $wrapNodes } from "@lexical/selection";
import {
  $findMatchingParent,
  $getNearestBlockElementAncestorOrThrow,
  $getNearestNodeOfType,
  mergeRegister,
} from "@lexical/utils";
import {
  $createParagraphNode,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  DEPRECATED_$isGridSelection,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from "lexical";
import { useCallback, useEffect, useState } from "react";
import * as React from "react";

import {
  Segment,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  Undo,
  Redo,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  Code,
  InsertLink,
  FormatStrikethrough,
  Subscript,
  Superscript,
  Clear,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  FormatIndentIncrease,
  FormatIndentDecrease,
} from "@mui/icons-material";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import useModal from "../../hooks/useModal";

import DropDown, { DropDownItem } from "../../ui/DropDown";
import { getSelectedNode } from "../../utils/getSelectedNode";
import { sanitizeUrl } from "@/components/Lexical/utils/sanitizeUrl";
import { IS_APPLE } from "@/components/Lexical/shared/environment";

const blockTypeToBlockName = {
  bullet: "Bulleted",
  check: "Check List",
  code: "Code Block",
  h1: "Heading 1",
  h2: "Heading 2",
  h3: "Heading 3",
  h4: "Heading 4",
  h5: "Heading 5",
  h6: "Heading 6",
  number: "Numbered",
  paragraph: "Normal",
  quote: "Quote",
};

function getCodeLanguageOptions(): [string, string][] {
  const options: [string, string][] = [];

  for (const [lang, friendlyName] of Object.entries(CODE_LANGUAGE_FRIENDLY_NAME_MAP)) {
    options.push([lang, friendlyName]);
  }

  return options;
}

const CODE_LANGUAGE_OPTIONS = getCodeLanguageOptions();

function dropDownActiveClass(active: boolean) {
  if (active) return "active dropdown-item-active";
  else return "";
}

function BlockFormatDropDown({
  editor,
  blockType,
  disabled = false,
}: {
  blockType: keyof typeof blockTypeToBlockName;
  editor: LexicalEditor;
  disabled?: boolean;
}): JSX.Element {
  const { t } = useTranslation("common");

  const formatParagraph = () => {
    if (blockType !== "paragraph") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection) || DEPRECATED_$isGridSelection(selection)) {
          $wrapNodes(selection, () => $createParagraphNode());
        }
      });
    }
  };

  const formatBulletList = () => {
    if (blockType !== "bullet") {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatNumberedList = () => {
    if (blockType !== "number") {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatQuote = () => {
    if (blockType !== "quote") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection) || DEPRECATED_$isGridSelection(selection)) {
          $wrapNodes(selection, () => $createQuoteNode());
        }
      });
    }
  };

  const formatCode = () => {
    if (blockType !== "code") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection) || DEPRECATED_$isGridSelection(selection)) {
          if (selection.isCollapsed()) {
            $wrapNodes(selection, () => $createCodeNode());
          } else {
            const textContent = selection.getTextContent();
            const codeNode = $createCodeNode();
            selection.insertNodes([codeNode]);
            selection.insertRawText(textContent);
          }
        }
      });
    }
  };

  return (
    <DropDown
      disabled={disabled}
      buttonClassName="toolbar-item block-controls"
      buttonIconClassName={`icon block-type ${blockType}`}
      buttonLabel={t(`components.Lexical.${blockType}`)}
      buttonAriaLabel={t(`components.Lexical.${blockType}`)}
    >
      <DropDownItem className={`item ${dropDownActiveClass(blockType === "paragraph")}`} onClick={formatParagraph}>
        <Segment />
        <span className="text">{t("components.Lexical.paragraph")}</span>
      </DropDownItem>
      <DropDownItem className={`item ${dropDownActiveClass(blockType === "bullet")}`} onClick={formatBulletList}>
        <FormatListBulleted />
        <span className="text">{t("components.Lexical.bullet")}</span>
      </DropDownItem>
      <DropDownItem className={`item ${dropDownActiveClass(blockType === "number")}`} onClick={formatNumberedList}>
        <FormatListNumbered />
        <span className="text">{t("components.Lexical.number")}</span>
      </DropDownItem>
      <DropDownItem className={`item ${dropDownActiveClass(blockType === "quote")}`} onClick={formatQuote}>
        <FormatQuote />
        <span className="text">{t("components.Lexical.quote")}</span>
      </DropDownItem>
      <DropDownItem className={`item ${dropDownActiveClass(blockType === "code")}`} onClick={formatCode}>
        <Code />
        <span className="text">{t("components.Lexical.code")}</span>
      </DropDownItem>
    </DropDown>
  );
}

function Divider(): JSX.Element {
  return <div className="divider" />;
}

export default function ToolbarPlugin(): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);
  const [blockType, setBlockType] = useState<keyof typeof blockTypeToBlockName>("paragraph");
  const [selectedElementKey, setSelectedElementKey] = useState<NodeKey | null>(null);
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [modal, showModal] = useModal();
  const [isRTL, setIsRTL] = useState(false);
  const [codeLanguage, setCodeLanguage] = useState<string>("");
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());
  const { t } = useTranslation("common");

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);

      // Update text format
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
      setIsSubscript(selection.hasFormat("subscript"));
      setIsSuperscript(selection.hasFormat("superscript"));
      setIsCode(selection.hasFormat("code"));
      setIsRTL($isParentElementRTL(selection));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      if (elementDOM !== null) {
        setSelectedElementKey(elementKey);
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
          const type = parentList ? parentList.getListType() : element.getListType();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType();
          if (type in blockTypeToBlockName) {
            setBlockType(type as keyof typeof blockTypeToBlockName);
          }
          if ($isCodeNode(element)) {
            const language = element.getLanguage() as keyof typeof CODE_LANGUAGE_MAP;
            setCodeLanguage(language ? CODE_LANGUAGE_MAP[language] || language : "");
            return;
          }
        }
      }
    }
  }, [activeEditor]);

  useEffect(
    () =>
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateToolbar();
          setActiveEditor(newEditor);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
    [editor, updateToolbar]
  );

  useEffect(
    () =>
      mergeRegister(
        editor.registerEditableListener((editable) => {
          setIsEditable(editable);
        }),
        activeEditor.registerUpdateListener(({ editorState }) => {
          editorState.read(() => {
            updateToolbar();
          });
        }),
        activeEditor.registerCommand<boolean>(
          CAN_UNDO_COMMAND,
          (payload) => {
            setCanUndo(payload);
            return false;
          },
          COMMAND_PRIORITY_CRITICAL
        ),
        activeEditor.registerCommand<boolean>(
          CAN_REDO_COMMAND,
          (payload) => {
            setCanRedo(payload);
            return false;
          },
          COMMAND_PRIORITY_CRITICAL
        )
      ),
    [activeEditor, editor, updateToolbar]
  );

  const clearFormatting = useCallback(() => {
    activeEditor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $selectAll(selection);
        selection.getNodes().forEach((node) => {
          if ($isTextNode(node)) {
            node.setFormat(0);
            node.setStyle("");
            $getNearestBlockElementAncestorOrThrow(node).setFormat("");
          }
          if ($isDecoratorBlockNode(node)) {
            node.setFormat("");
          }
        });
      }
    });
  }, [activeEditor]);

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl("https://"));
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  const onCodeLanguageSelect = useCallback(
    (value: string) => {
      activeEditor.update(() => {
        if (selectedElementKey !== null) {
          const node = $getNodeByKey(selectedElementKey);
          if ($isCodeNode(node)) {
            node.setLanguage(value);
          }
        }
      });
    },
    [activeEditor, selectedElementKey]
  );

  return (
    <ToolbarWrapper className="toolbar">
      <button
        disabled={!canUndo || !isEditable}
        onClick={(event) => {
          event.preventDefault();
          activeEditor.dispatchCommand(UNDO_COMMAND, undefined);
        }}
        title={IS_APPLE ? "Undo (⌘Z)" : "Undo (Ctrl+Z)"}
        className="toolbar-item spaced"
        aria-label="Undo"
      >
        <Undo />
      </button>
      <button
        disabled={!canRedo || !isEditable}
        onClick={(event) => {
          event.preventDefault();
          activeEditor.dispatchCommand(REDO_COMMAND, undefined);
        }}
        title={IS_APPLE ? "Redo (⌘Y)" : "Redo (Ctrl+Y)"}
        className="toolbar-item"
        aria-label="Redo"
      >
        <Redo />
      </button>
      <Divider />
      {blockType in blockTypeToBlockName && activeEditor === editor && (
        <>
          <BlockFormatDropDown disabled={!isEditable} blockType={blockType} editor={editor} />
          <Divider />
        </>
      )}
      {blockType === "code" ? (
        <>
          <DropDown
            disabled={!isEditable}
            buttonClassName="toolbar-item code-language"
            buttonLabel={getLanguageFriendlyName(codeLanguage)}
            buttonAriaLabel="Select language"
          >
            {CODE_LANGUAGE_OPTIONS.map(([value, name]) => (
              <DropDownItem
                className={`item ${dropDownActiveClass(value === codeLanguage)}`}
                onClick={() => onCodeLanguageSelect(value)}
                key={value}
              >
                <span className="text">{name}</span>
              </DropDownItem>
            ))}
          </DropDown>
        </>
      ) : (
        <>
          <button
            disabled={!isEditable}
            onClick={(event) => {
              event.preventDefault();
              activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
            }}
            className={`toolbar-item spaced ${isBold ? "active" : ""}`}
            title={t("components.Lexical.shortcutBold") + (IS_APPLE ? " (⌘B)" : " (Ctrl+B)")}
            aria-label={t("components.Lexical.shortcutBold") + (IS_APPLE ? "⌘B" : "Ctrl+B")}
          >
            <FormatBold />
          </button>
          <button
            disabled={!isEditable}
            onClick={(event) => {
              event.preventDefault();
              activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
            }}
            className={`toolbar-item spaced ${isItalic ? "active" : ""}`}
            title={t("components.Lexical.shortcutItalic") + (IS_APPLE ? " (⌘I)" : " (Ctrl+I)")}
            aria-label={t("components.Lexical.shortcutItalic") + (IS_APPLE ? "⌘I" : "Ctrl+I")}
          >
            <FormatItalic />
          </button>
          <button
            disabled={!isEditable}
            onClick={(event) => {
              event.preventDefault();
              activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
            }}
            className={`toolbar-item spaced ${isUnderline ? "active" : ""}`}
            title={t("components.Lexical.underlined") + (IS_APPLE ? "(⌘U)" : "(Ctrl+U)")}
            aria-label={t("components.Lexical.shortcutUnderlined") + (IS_APPLE ? "⌘U" : "Ctrl+U")}
          >
            <FormatUnderlined />
          </button>
          <button
            disabled={!isEditable}
            onClick={(event) => {
              event.preventDefault();
              activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "code");
            }}
            className={`toolbar-item spaced ${isCode ? "active" : ""}`}
            title={t("components.Lexical.insertCodeBlock")}
            aria-label={t("components.Lexical.insertCodeBlock")}
          >
            <Code />
          </button>
          <button
            disabled={!isEditable}
            onClick={(event) => {
              event.preventDefault();
              insertLink();
            }}
            className={`toolbar-item spaced ${isLink ? "active" : ""}`}
            aria-label={t("components.Lexical.insertLink")}
            title={t("components.Lexical.insertLink")}
          >
            <InsertLink />
          </button>
          <DropDown
            disabled={!isEditable}
            buttonClassName="toolbar-item spaced"
            buttonLabel="Aa"
            buttonIconClassName="icon dropdown-more"
          >
            <DropDownItem
              onClick={() => {
                activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
              }}
              className={`item ${dropDownActiveClass(isStrikethrough)}`}
              title={t("components.Lexical.strikethrough")}
              aria-label={t("components.Lexical.strikethrough")}
            >
              <FormatStrikethrough />
              <span className="text">{t("components.Lexical.strikethrough")}</span>
            </DropDownItem>
            <DropDownItem
              onClick={() => {
                activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "subscript");
              }}
              className={`item ${dropDownActiveClass(isSubscript)}`}
              title={t("components.Lexical.subscript")}
              aria-label="Format text with a subscript"
            >
              <Subscript />
              <span className="text">{t("components.Lexical.subscript")}</span>
            </DropDownItem>
            <DropDownItem
              onClick={() => {
                activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "superscript");
              }}
              className={`item ${dropDownActiveClass(isSuperscript)}`}
              title={t("components.Lexical.superscript")}
              aria-label="Format text with a superscript"
            >
              <Superscript />
              <span className="text">{t("components.Lexical.superscript")}</span>
            </DropDownItem>
            <DropDownItem
              onClick={clearFormatting}
              className="item"
              title={t("components.Lexical.clearFormat")}
              aria-label="Clear all text formatting"
            >
              <Clear />
              <span className="text">{t("components.Lexical.clearFormat")}</span>
            </DropDownItem>
          </DropDown>
        </>
      )}
      <Divider />
      <DropDown
        disabled={!isEditable}
        buttonLabel={t("components.Lexical.align")}
        buttonIconClassName="icon left-align"
        buttonClassName="toolbar-item spaced alignment"
        buttonAriaLabel="Formatting options for text alignment"
      >
        <DropDownItem
          onClick={() => {
            activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left");
          }}
          className="item"
        >
          <FormatAlignLeft />
          <span className="text">{t("components.Lexical.leftAlign")}</span>
        </DropDownItem>
        <DropDownItem
          onClick={() => {
            activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center");
          }}
          className="item"
        >
          <FormatAlignCenter />
          <span className="text">{t("components.Lexical.centerAlign")}</span>
        </DropDownItem>
        <DropDownItem
          onClick={() => {
            activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right");
          }}
          className="item"
        >
          <FormatAlignRight />
          <span className="text">{t("components.Lexical.rightAlign")}</span>
        </DropDownItem>
        <DropDownItem
          onClick={() => {
            activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "justify");
          }}
          className="item"
        >
          <FormatAlignJustify />
          <span className="text">{t("components.Lexical.justifyAlign")}</span>
        </DropDownItem>
        <Divider />
        <DropDownItem
          onClick={() => {
            activeEditor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
          }}
          className="item"
        >
          {isRTL ? <FormatIndentIncrease /> : <FormatIndentDecrease />}
          <span className="text">{t("components.Lexical.outdent")}</span>
        </DropDownItem>
        <DropDownItem
          onClick={() => {
            activeEditor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
          }}
          className="item"
        >
          {isRTL ? <FormatIndentDecrease /> : <FormatIndentIncrease />}
          <span className="text">{t("components.Lexical.indent")}</span>
        </DropDownItem>
      </DropDown>
      {modal}
    </ToolbarWrapper>
  );
}

const ToolbarWrapper = styled("div")`
  display: flex;
  flex-wrap: wrap;
  background: #fff;
  padding: 8px;
  margin-bottom: -4px;
  border-top: 2px solid #cdcdcd;
  border-left: 2px solid #cdcdcd;
  border-right: 2px solid #cdcdcd;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  vertical-align: middle;
`;
